import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import { PriorityQueueListResponse } from '@utils/types/back-office/priority-queue';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export const useGetPriorityQueue = () => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();
  const [sp] = useSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  // Parse all DataGrid column-filter q params (field:op:value)
  const parsedQ = (() => {
    const result: Record<string, string> = {};
    for (const entry of sp.getAll('q')) {
      const c1 = entry.indexOf(':');
      if (c1 === -1) continue;
      const field = entry.slice(0, c1);
      const rest = entry.slice(c1 + 1);
      const c2 = rest.indexOf(':');
      if (c2 === -1) continue;
      const value = rest
        .slice(c2 + 1)
        .replace(/^%/, '')
        .replace(/%$/, '');
      if (value) result[field] = value;
    }
    return result;
  })();

  const status =
    (searchParams.status as string | undefined) ||
    parsedQ['status'] ||
    undefined;
  const stuckOnly = searchParams.stuck_only === 'true';
  const readyForRetry = searchParams.ready_for_retry === 'true';
  const includePayload = searchParams.include_payload === 'true';
  const zoomId =
    (searchParams.zoom_id as string | undefined) ||
    parsedQ['zoom_id'] ||
    undefined;
  const itemId = parsedQ['id'] ? Number(parsedQ['id']) : undefined;
  // DataGrid uses 'priority' field name but filterKey 'org_priority'; API expects 'org_priority'
  const orgPriority = searchParams.org_priority
    ? Number(searchParams.org_priority)
    : searchParams.priority
      ? Number(searchParams.priority)
      : parsedQ['org_priority']
        ? Number(parsedQ['org_priority'])
        : undefined;
  const organizationId = searchParams.organization_id
    ? Number(searchParams.organization_id)
    : undefined;
  const organizationName =
    (searchParams.organization_name as string | undefined) ||
    parsedQ['organization_name'] ||
    undefined;

  const fetchQueue = async () => {
    const { priorityQueue } = GET_URL_NEW({
      offset,
      limit,
      sort: sortField,
      direction: sortDirection
    });

    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (stuckOnly) params.append('stuck_only', 'true');
    if (readyForRetry) params.append('ready_for_retry', 'true');
    if (includePayload) params.append('include_payload', 'true');
    if (zoomId) params.append('zoom_id', zoomId);
    if (itemId !== undefined) params.append('item_id', String(itemId));
    if (orgPriority !== undefined)
      params.append('org_priority', String(orgPriority));
    if (organizationId !== undefined)
      params.append('organization_id', String(organizationId));
    if (organizationName) params.append('organization_name', organizationName);

    const qs = params.toString();
    const joiner = priorityQueue.includes('?') ? '&' : '?';
    const url = qs ? `${priorityQueue}${joiner}${qs}` : priorityQueue;

    const { data } = await Axios.get<PriorityQueueListResponse>(url);
    return data;
  };

  return useQuery<PriorityQueueListResponse, AxiosError<BaseError>>(
    [
      'priorityQueue',
      offset,
      limit,
      sortField,
      sortDirection,
      status,
      stuckOnly,
      readyForRetry,
      includePayload,
      zoomId,
      itemId,
      orgPriority,
      organizationId,
      organizationName
    ],
    fetchQueue,
    {
      useErrorBoundary: false,
      keepPreviousData: true,
      onError: (error: AxiosError<BaseError>) => {
        const d = error.response?.data as Record<string, unknown>;
        const msg =
          (d?.error_description as string) ||
          (d?.detail as string) ||
          (d?.error as string) ||
          (d?.message as string) ||
          t('an-error-occurred');
        toast.error(String(msg));
      }
    }
  );
};
